import React from 'react';
import { Zap, FileText, Clock, CheckCircle2, CreditCard, Timer, Database, Bot, Search, FileEdit, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface PremiumFeaturesProps {
  onPaymentClick?: () => void;
}

export default function PremiumFeatures({ onPaymentClick }: PremiumFeaturesProps) {
  const navigate = useNavigate();
  
  const handlePaymentClick = (plan: 'premium' | 'standard' | 'day') => {
    // Always navigate to the payment page with the selected plan
    navigate(`/premium?plan=${plan}`);
  };
  const dayPassFeatures = [
    {
      icon: <Zap className="w-6 sm:w-8 h-6 sm:h-8" />,
      title: "Verktyg för 24 timmar",
      description: "Få tillgång till alla våra tidsbesparande verktyg för en hel dag och spara veckor av manuellt arbete."
    },
    {
      icon: <Search className="w-6 sm:w-8 h-6 sm:h-8" />,
      title: "Optimerad söklogik",
      description: "Arbeta med många ansökningar samtidigt och hitta relevanta stiftelser snabbare med vår optimerade sökmotor."
    },
    {
      icon: <Bot className="w-6 sm:w-8 h-6 sm:h-8" />,
      title: "Webbbaserade AI-verktyg",
      description: "Extrahera information som inte finns tillgängligt i stiftelsedatabasen genom våra smarta webb-baserade AI-lösningar."
    }
  ];

  const monthlySubscriptionFeatures = [
    {
      icon: <Bot className="w-6 sm:w-8 h-6 sm:h-8" />,
      title: "Ansökningshjälpen AI",
      description: "Ökar dina chanser för att lyckas med dina ansökningar väsentligt. Omistbart för dig som ansöker mycket och ofta."
    },
    {
      icon: <FileEdit className="w-6 sm:w-8 h-6 sm:h-8" />,
      title: "Personlig ansökningshjälp",
      description: "AI-assistans för att skriva övertygande ansökningar specifikt anpassade efter varje stiftelse."
    },
    {
      icon: <FileText className="w-6 sm:w-8 h-6 sm:h-8" />,
      title: "Smarta sökningar",
      description: "Hitta stiftelser som passar din situation perfekt genom AI-baserad matchning och djupgående analys."
    },
    {
      icon: <Save className="w-6 sm:w-8 h-6 sm:h-8" />,
      title: "Personlig databas",
      description: "Din egen sida där du kan spara stiftelser, ansökningar, och hålla koll på viktiga deadlines."
    },
    {
      icon: <Database className="w-6 sm:w-8 h-6 sm:h-8" />,
      title: "Omfattande informationsutvinning",
      description: "Hitta kontaktuppgifter och information om stiftelser som inte finns i den traditionella databasen."
    },
    {
      icon: <CheckCircle2 className="w-6 sm:w-8 h-6 sm:h-8" />,
      title: "Avbryt när som helst",
      description: "Flexibel prenumeration utan bindningstid - avsluta när du vill."
    }
  ];

  return (
    <div className="bg-gradient-to-br from-[#A3C1DA] to-[#D1B3C4] text-white rounded-2xl shadow-xl p-4 sm:p-8 mb-12 transform hover:scale-[1.02] transition-all duration-300">
      <h3 className="text-xl sm:text-2xl font-bold mb-6 sm:mb-8 text-center">
        Våra premiumalternativ
      </h3>
      <div className="text-center mb-4">
        <p className="text-white/90 text-sm">Välj det alternativ som passar dig bäst</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Day Pass Option */}
        <div className="bg-white/10 p-6 rounded-xl border border-white/20 h-full flex flex-col">
          <div className="mb-4">
            <h4 className="text-xl font-bold">Dagspass</h4>
            <p className="text-white/80 text-sm mt-1">Perfekt för dig som behöver hjälp just nu</p>
            <div className="mt-3 text-center">
              <span className="text-3xl font-bold">99 kr</span>
              <span className="text-white/80 ml-2">för 24 timmar</span>
            </div>
          </div>
          
          <ul className="mt-3 space-y-1 flex-grow">
            <li className="flex items-center text-sm">
              <div className="w-4 h-4 mr-2 bg-white/20 rounded-full flex items-center justify-center text-white text-xs">✓</div>
              <span>Avancerad sökning i 24 timmar</span>
            </li>
            <li className="flex items-center text-sm">
              <div className="w-4 h-4 mr-2 bg-white/20 rounded-full flex items-center justify-center text-white text-xs">✓</div>
              <span>Djupanalys (90 stiftelser)</span>
            </li>
            <li className="flex items-center text-sm">
              <div className="w-4 h-4 mr-2 bg-white/20 rounded-full flex items-center justify-center text-white text-xs">✓</div>
              <span>Tillgång till e-post & webbinfo</span>
            </li>
            <li className="flex items-center text-sm">
              <div className="w-4 h-4 mr-2 bg-white/20 rounded-full flex items-center justify-center text-white text-xs">✓</div>
              <span>Information om stiftelsers aktivitet</span>
            </li>
          </ul>
          <div className="mt-4 flex flex-col items-center">
            <button
              onClick={() => handlePaymentClick('day')}
              className="w-full max-w-[200px] px-4 py-2 bg-white text-[#A3C1DA] rounded-xl hover:bg-[#F2C1D1] hover:text-white transition-all duration-300 font-medium"
            >
              Aktivera dagspass
            </button>
            <p className="mt-2 text-xs text-center text-white/80 invisible">Avbryt när som helst</p>
          </div>
        </div>
        
        {/* Standard Option - Most Popular */}
        <div className="bg-white/10 p-6 rounded-xl border border-white/20 relative h-full flex flex-col">
          <div className="absolute -top-3 right-6 bg-[#F2C1D1] px-3 py-1 rounded-full text-sm font-bold">
            Mest populär
          </div>
          
          <div className="mb-4">
            <h4 className="text-xl font-bold">Standard</h4>
            <p className="text-white/80 text-sm mt-1">Perfekt för dig som söker stiftelsemedel regelbundet</p>
            <div className="mt-3 text-center">
              <span className="text-3xl font-bold">299 kr</span>
              <span className="text-white/80 ml-2">per månad</span>
            </div>
          </div>
          
          <ul className="mt-3 space-y-1 flex-grow">
            <li className="flex items-center text-sm">
              <div className="w-4 h-4 mr-2 bg-white/20 rounded-full flex items-center justify-center text-white text-xs">✓</div>
              <span>AI-driven matchning</span>
            </li>
            <li className="flex items-center text-sm">
              <div className="w-4 h-4 mr-2 bg-white/20 rounded-full flex items-center justify-center text-white text-xs">✓</div>
              <span>Djupanalys (300 stiftelser/månad)</span>
            </li>
            <li className="flex items-center text-sm">
              <div className="w-4 h-4 mr-2 bg-white/20 rounded-full flex items-center justify-center text-white text-xs">✓</div>
              <span>E-post & webbinfo</span>
            </li>
            <li className="flex items-center text-sm">
              <div className="w-4 h-4 mr-2 bg-white/20 rounded-full flex items-center justify-center text-white text-xs">✓</div>
              <span>Stiftelseaktivitet</span>
            </li>
            <li className="flex items-center text-sm">
              <div className="w-4 h-4 mr-2 bg-white/20 rounded-full flex items-center justify-center text-white text-xs">✓</div>
              <span>Organisationsverktyg</span>
            </li>
          </ul>
          
          <div className="mt-4 flex flex-col items-center">
            <button
              onClick={() => handlePaymentClick('standard')}
              className="w-full max-w-[200px] px-4 py-2 bg-white text-[#A3C1DA] rounded-xl hover:bg-[#F2C1D1] hover:text-white transition-all duration-300 font-medium"
            >
              Välj standard
            </button>
            <p className="mt-2 text-xs text-center text-white/80">Avbryt när som helst</p>
          </div>
        </div>
        
        {/* Premium Option */}
        <div className="bg-white/10 p-6 rounded-xl border border-white/20 h-full flex flex-col">
          <div className="mb-4">
            <h4 className="text-xl font-bold">Premium</h4>
            <p className="text-white/80 text-sm mt-1">Ökar dina chanser för framgångsrika ansökningar</p>
            <div className="mt-3 text-center">
              <span className="text-3xl font-bold">499 kr</span>
              <span className="text-white/80 ml-2">per månad</span>
            </div>
          </div>
          
          <ul className="mt-3 space-y-1 flex-grow">
            <li className="flex items-center text-sm">
              <div className="w-4 h-4 mr-2 bg-white/20 rounded-full flex items-center justify-center text-white text-xs">✓</div>
              <span>Allt i Standard-paketet</span>
            </li>
            <li className="flex items-center text-sm">
              <div className="w-4 h-4 mr-2 bg-white/20 rounded-full flex items-center justify-center text-white text-xs">✓</div>
              <span>Ansökningshjälpen AI</span>
            </li>
            <li className="flex items-center text-sm">
              <div className="w-4 h-4 mr-2 bg-white/20 rounded-full flex items-center justify-center text-white text-xs">✓</div>
              <span>Obegränsad djupanalys</span>
            </li>
            <li className="flex items-center text-sm">
              <div className="w-4 h-4 mr-2 bg-white/20 rounded-full flex items-center justify-center text-white text-xs">✓</div>
              <span>Ansökningstider</span>
            </li>
            <li className="flex items-center text-sm">
              <div className="w-4 h-4 mr-2 bg-white/20 rounded-full flex items-center justify-center text-white text-xs">✓</div>
              <span>Ansökningsmalllar</span>
            </li>
            <li className="flex items-center text-sm">
              <div className="w-4 h-4 mr-2 bg-white/20 rounded-full flex items-center justify-center text-white text-xs">✓</div>
              <span>Prioriterad support</span>
            </li>
          </ul>
          
          <div className="mt-4 flex flex-col items-center">
            <button
              onClick={() => handlePaymentClick('premium')}
              className="w-full max-w-[200px] px-4 py-2 bg-white text-[#A3C1DA] rounded-xl hover:bg-[#F2C1D1] hover:text-white transition-all duration-300 font-medium"
            >
              Välj premium
            </button>
            <p className="mt-2 text-xs text-center text-white/80">Avbryt när som helst</p>
          </div>
        </div>
      </div>
    </div>
  );
}
